import React from 'react';

// Style constants
const COLORS = {
  NEUTRAL_BG: '#F8F9FA',
  NEUTRAL_WHITE: '#FFFFFF',
  NEUTRAL_TEXT: '#1A1A1A',
  ACCENT_GRAY: '#6B7280',
  PRIMARY_BROWN: '#8B4513'
};

const FONTS = {
  body: { fontFamily: 'Inter, system-ui, sans-serif' }
};

export function Pagination({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  startIndex, 
  endIndex, 
  totalItems,
  itemsPerPage = 10
}) {
  if (totalPages <= 1) {
    return null;
  }

  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      padding: '16px 0'
    }}>
      {/* Results Summary */}
      <div style={{ 
        fontSize: 12, 
        color: COLORS.ACCENT_GRAY,
        ...FONTS.body
      }}>
        Showing {startIndex + 1}-{endIndex} of {totalItems} products
      </div>

      {/* Pagination Controls */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: 8 
      }}>
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          style={{
            background: currentPage === 1 ? COLORS.NEUTRAL_BG : COLORS.NEUTRAL_WHITE,
            color: currentPage === 1 ? COLORS.ACCENT_GRAY : COLORS.NEUTRAL_TEXT,
            border: '1px solid #E0E0E0',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            ...FONTS.body
          }}
          aria-label="Go to previous page"
        >
          Previous
        </button>

        {/* Page Numbers */}
        {visiblePages.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span style={{
                padding: '8px 12px',
                fontSize: 12,
                color: COLORS.ACCENT_GRAY,
                ...FONTS.body
              }}>
                ...
              </span>
            ) : (
              <button
                onClick={() => onPageChange(page)}
                style={{
                  background: currentPage === page ? COLORS.PRIMARY_BROWN : COLORS.NEUTRAL_WHITE,
                  color: currentPage === page ? COLORS.NEUTRAL_WHITE : COLORS.NEUTRAL_TEXT,
                  border: '1px solid #E0E0E0',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: 40,
                  ...FONTS.body
                }}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}

        {/* Next Button */}
        <button
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          style={{
            background: currentPage === totalPages ? COLORS.NEUTRAL_BG : COLORS.NEUTRAL_WHITE,
            color: currentPage === totalPages ? COLORS.ACCENT_GRAY : COLORS.NEUTRAL_TEXT,
            border: '1px solid #E0E0E0',
            borderRadius: 8,
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            ...FONTS.body
          }}
          aria-label="Go to next page"
        >
          Next
        </button>
      </div>
    </div>
  );
}
